/**
 * Shared recursive traversal of a workflow's action tree.
 *
 * Logic Apps actions nest in several shapes — Scope/Foreach/Until expose a
 * child `actions` map, `If` adds an `else.actions` branch, and `Switch` fans
 * out into `cases[*].actions` plus `default.actions`. Several call sites need
 * to visit *every* action regardless of nesting, and historically each
 * re-implemented (or forgot) this walk, producing divergent behaviour — e.g.
 * mock seeding recursed while connection-mapping extraction did not.
 *
 * `walkAllActions` is the single source of truth for that traversal. The
 * visitor receives:
 *   - `names`   — candidate identifier(s) for template-mock lookup. For regular
 *                 actions this is `[name]`; for agent tools it is
 *                 `[toolName, innerActionName]` so a template can key by either.
 *   - `action`  — the concrete action object whose `inputs` should be
 *                 inspected. For tools this is the *inner* action (not the
 *                 wrapping agent) so connection extraction sees its real
 *                 `host` / `serviceProviderConfiguration`.
 *   - `scopedName` — the canvas-scoped path used as `actionName` in mocks
 *                 (`Parent/Child` for nested actions; just `innerActionName`
 *                 for tools, matching the canvas node).
 *   - `nodeId`  — the canvas node id, ready to use as a store key. Regular
 *                 actions → `action-${scopedName}`; agent tools →
 *                 `tool-${agentName}-${innerActionName}` (matches the id the
 *                 canvas builds in WorkflowDesigner / SubWorkflowPreview /
 *                 workflowStore).
 */
export interface ActionTreeLocation<TAction extends object = Record<string, unknown>> {
  name: string;
  action: TAction;
  container: Record<string, TAction>;
  /** Dot path to the actions map containing this action. */
  path: string;
  /** Dot path to this specific action. */
  actionPath: string;
  /** Slash-delimited canvas path for nested actions. */
  scopedName: string;
}

type ActionTreeVisitor<TAction extends object> = (location: ActionTreeLocation<TAction>) => void;

function walkActionTree<TAction extends object>(
  actions: Record<string, TAction> | undefined,
  visitor: ActionTreeVisitor<TAction>,
  afterChildren?: ActionTreeVisitor<TAction>,
  parentPath = '',
  containerPath = 'actions',
): void {
  if (!actions) return;

  for (const [name, actionValue] of Object.entries(actions)) {
    if (!actionValue || typeof actionValue !== 'object') continue;

    const action = actionValue as unknown as Record<string, unknown>;
    const scopedName = parentPath ? `${parentPath}/${name}` : name;
    const location: ActionTreeLocation<TAction> = {
      name,
      action: actionValue,
      container: actions,
      path: containerPath,
      actionPath: `${containerPath}.${name}`,
      scopedName,
    };

    visitor(location);

    // Scope / Foreach / Until / If true branch — direct child actions.
    if (action.actions && typeof action.actions === 'object') {
      walkActionTree(
        action.actions as Record<string, TAction>,
        visitor,
        afterChildren,
        scopedName,
        `${containerPath}.${name}.actions`,
      );
    }
    // If — the else branch.
    if (action.else && typeof action.else === 'object') {
      const elseBranch = action.else as Record<string, unknown>;
      if (elseBranch.actions && typeof elseBranch.actions === 'object') {
        walkActionTree(
          elseBranch.actions as Record<string, TAction>,
          visitor,
          afterChildren,
          scopedName,
          `${containerPath}.${name}.else.actions`,
        );
      }
    }
    // Switch — each case branch.
    if (action.cases && typeof action.cases === 'object') {
      for (const [caseName, caseValue] of Object.entries(action.cases as Record<string, unknown>)) {
        if (!caseValue || typeof caseValue !== 'object') continue;
        const typedCase = caseValue as Record<string, unknown>;
        if (typedCase.actions && typeof typedCase.actions === 'object') {
          walkActionTree(
            typedCase.actions as Record<string, TAction>,
            visitor,
            afterChildren,
            scopedName,
            `${containerPath}.${name}.cases.${caseName}.actions`,
          );
        }
      }
    }
    // Switch — the default branch.
    if (action.default && typeof action.default === 'object') {
      const defaultBranch = action.default as Record<string, unknown>;
      if (defaultBranch.actions && typeof defaultBranch.actions === 'object') {
        walkActionTree(
          defaultBranch.actions as Record<string, TAction>,
          visitor,
          afterChildren,
          scopedName,
          `${containerPath}.${name}.default.actions`,
        );
      }
    }

    afterChildren?.(location);
  }
}

/**
 * Find every own action property matching a name across all control-flow
 * branches. Returning all matches lets callers reject ambiguous workflows.
 */
export function findActionLocations<TAction extends object>(
  actions: Record<string, TAction> | undefined,
  name: string,
  basePath = 'actions',
): ActionTreeLocation<TAction>[] {
  const locations: ActionTreeLocation<TAction>[] = [];
  walkActionTree(
    actions,
    (location) => {
      if (location.name === name && Object.hasOwn(location.container, name)) {
        locations.push(location);
      }
    },
    undefined,
    '',
    basePath,
  );
  return locations;
}

export function walkAllActions(
  actions: Record<string, unknown> | undefined,
  visitor: (
    names: string[],
    action: Record<string, unknown>,
    scopedName: string,
    nodeId: string,
  ) => void,
  parentPath = '',
): void {
  walkActionTree<Record<string, unknown>>(
    actions as Record<string, Record<string, unknown>> | undefined,
    ({ name, action, scopedName }) => {
      visitor([name], action, scopedName, `action-${scopedName}`);
    },
    ({ name, action }) => {
      // Agent tools — nested under the agent's `tools` property. Each tool wraps
      // one or more inner actions and is rendered on canvas as a single tool
      // node with id `tool-{agentName}-{innerActionName}`.
      if (!action.tools || typeof action.tools !== 'object') return;

      const agentName = name;
      for (const [toolName, toolValue] of Object.entries(action.tools as Record<string, unknown>)) {
        if (!toolValue || typeof toolValue !== 'object') continue;
        const tool = toolValue as Record<string, unknown>;
        const toolActions = tool.actions as Record<string, unknown> | undefined;
        const innerEntries = toolActions ? Object.entries(toolActions) : [];
        const [innerActionName, innerActionValue] = innerEntries[0] ?? [toolName, undefined];
        const innerAction =
          innerActionValue && typeof innerActionValue === 'object'
            ? (innerActionValue as Record<string, unknown>)
            : ({} as Record<string, unknown>);

        visitor(
          [toolName, innerActionName],
          innerAction,
          innerActionName,
          `tool-${agentName}-${innerActionName}`,
        );
      }
    },
    parentPath,
  );
}
